import { UserJourney, UserJourneyData } from '@grammarly/user-journey'
import { BrowserService } from '../browser'
import { UserService } from '../user'

const isExtensionInstalled = () => IS_CLIENT_SIDE && !!window.GR_EXTENSION_ID

const llamaWindowsGroup = 'windowsExtension'
const llamaMacGroup = 'macExtension'
const extensionGroup = 'extension-installed'

export interface UserJourneyService {
  hasInstalledPersistentClient: boolean
  hasLlamaInstalled: boolean
  hasExtensionInstalled: boolean
  signupSuccessTimestamp: number | undefined
  trackPageVisit: () => void
  getFullUserJourney: () => UserJourneyData
}

export class UserJourneyServiceImpl implements UserJourneyService {
  private readonly _userService: UserService
  private readonly _browserService: BrowserService

  private _userJourney: UserJourney

  constructor(userService: UserService, browserService: BrowserService) {
    this._userJourney = new UserJourney()
    this._userService = userService
    this._browserService = browserService
  }

  private _hasPersistentClientUserGroup = (): boolean => {
    const { currentUser: user } = this._userService
    const { isWindows, isMac, isIOS } = this._browserService
    const llamaTargetGroup = isWindows ? llamaWindowsGroup : isMac && !isIOS ? llamaMacGroup : undefined
    const hasLlamaUserGroup = !!llamaTargetGroup && user.groups.includes(llamaTargetGroup)
    const hasExtensionUserGroup = user.groups.includes(extensionGroup)

    return hasLlamaUserGroup || hasExtensionUserGroup
  }

  trackPageVisit = () => {
    return this._userJourney.trackPageVisit()
  }

  getFullUserJourney = () => {
    return this._userJourney.getFullUserJourney()
  }

  get signupSuccessTimestamp(): number | undefined {
    return this._userJourney.getFullUserJourney().signupSuccessTimestamp
  }

  get hasInstalledPersistentClient(): boolean {
    if (isExtensionInstalled()) {
      return true
    }
    if (this._hasPersistentClientUserGroup()) {
      return true
    }
    return !!this._userJourney.getFullUserJourney().hasVisitedPersistentClientPostInstallPage
  }

  get hasLlamaInstalled(): boolean {
    const { currentUser: user } = this._userService
    const { isWindows, isMac, isIOS } = this._browserService
    const llamaTargetGroup = isWindows ? llamaWindowsGroup : isMac && !isIOS ? llamaMacGroup : undefined
    return !!llamaTargetGroup && user.groups.includes(llamaTargetGroup)
  }

  get hasExtensionInstalled(): boolean {
    // First check if extension is actually installed in browser
    if (isExtensionInstalled()) {
      return true
    }
    // Fall back to user group check
    const { currentUser: user } = this._userService
    return user.groups.includes(extensionGroup)
  }
}
